import React from 'react'

const steps = [
  {
    id: 1,
    title: 'Discover & Plan',
    desc: 'Deep-dive into business logic, technology stack selection, and cloud architecture blueprinting.',
    color: 'from-[#0b3b8f] to-[#1e3a8a]',
    icon: <img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/jira/jira-original.svg" className="w-12 h-12" alt="Jira" />
  },
  {
    id: 2,
    title: 'Design & Prototype',
    desc: 'Wireframing, UI/UX mocked flows, and API contract agreements before writing code.',
    color: 'from-[#1e3a8a] to-[#4b00cc]',
    icon: <img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/figma/figma-original.svg" className="w-12 h-12" alt="Figma" />
  },
  {
    id: 3,
    title: 'Agile Development',
    desc: 'Iterative, sprint-based full-stack engineering with continuous integration (CI) embedded.',
    color: 'from-[#4b00cc] to-[#0050ee]',
    icon: <img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" className="w-12 h-12" alt="React" />
  },
  {
    id: 4,
    title: 'QA & Security',
    desc: 'Rigorous automated testing, vulnerability scanning, and heavy load performance profiling.',
    color: 'from-[#0050ee] to-[#00cfff]',
    icon: <img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/selenium/selenium-original.svg" className="w-12 h-12" alt="Selenium" />
  },
  {
    id: 5,
    title: 'Deploy & Monitor',
    desc: 'Zero-downtime automated deployment to production (CD) via Docker, Kubernetes, and Cloud configs.',
    color: 'from-[#00cfff] to-[#0b3b8f]',
    icon: <img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg" className="w-12 h-12" alt="Docker" />
  }
]

export default function DevelopmentLifecycle() {
  return (
    <section className="relative py-28 md:py-36 bg-slate-50 font-inter overflow-hidden border-t border-slate-200/50 min-h-[500px] lg:min-h-[650px] flex items-center">
      <div className="container mx-auto px-4 max-w-6xl relative z-10">
        
        {/* Header */}
        <div className="text-center mb-16 md:mb-24">
          <div className="inline-flex items-center gap-2 bg-[#00cfff]/10 border border-[#00cfff]/20 px-5 py-2 rounded-full mb-6 shadow-sm">
            <span className="w-2.5 h-2.5 rounded-full bg-[#0b3b8f] animate-ping"></span>
            <span className="text-xs font-extrabold tracking-widest text-[#0b3b8f] uppercase">Production Pipeline</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-[#0b3b8f] tracking-tight leading-tight mb-6 mt-4">
            Enterprise Development Lifecycle at <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0b3b8f] to-[#00cfff]">NexGenium</span>
          </h2>
          <p className="text-lg text-slate-600 font-medium max-w-3xl mx-auto leading-relaxed">
            We don't just build software. We engineer automated, scalable systems utilizing an industry-standard Continuous Integration and Continuous Deployment (CI/CD) methodology.
          </p>
        </div>

        {/* Dynamic Pipeline Grid */}
        <div className="relative">
          
          {/* Connecting Line (Desktop Only) */}
          <div className="hidden lg:block absolute top-[45px] left-[10%] right-[10%] h-[2px] bg-slate-200 -z-10">
            <div className="absolute top-0 left-0 h-full w-full bg-gradient-to-r from-[#0b3b8f] via-[#00cfff] to-[#0b3b8f] animate-[gradientSlide_3s_linear_infinite] opacity-50" style={{ backgroundSize: '200% 100%' }}></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-4 relative">
            {steps.map((step, index) => (
              <div key={step.id} className="relative group perspective-1000 pt-6">
                
                {/* External Step Number Node */}
                <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-gradient-to-br ${step.color} shadow-lg shadow-blue-900/20 text-white flex items-center justify-center font-black text-sm z-20 ring-4 ring-slate-50`}>
                  {step.id}
                </div>

                {/* Brand Logo Node */}
                <div className="mx-auto w-24 h-24 rounded-2xl bg-white shadow-xl shadow-slate-200/50 flex flex-col items-center justify-center border-2 border-slate-100 mb-8 relative z-10 transition-transform duration-500 group-hover:-translate-y-2 group-hover:border-[#00cfff]/50 overflow-hidden">
                  <div className={`relative z-10 group-hover:scale-110 transition-transform duration-500`}>
                    {step.icon}
                  </div>
                </div>

                {/* Content Box */}
                <div className="bg-white rounded-3xl p-6 text-center border border-slate-100 shadow-[0_2px_15px_rgba(0,0,0,0.03)] hover:shadow-2xl transition-all duration-500 relative before:absolute before:inset-0 before:rounded-3xl before:bg-gradient-to-b before:from-transparent before:to-slate-50/50 before:-z-10 overflow-hidden">
                  {/* Hover Accent Line */}
                  <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${step.color} transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500`}></div>
                  
                  <h3 className="text-xl font-bold text-slate-800 mb-4 group-hover:text-[#0b3b8f] transition-colors">{step.title}</h3>
                  <p className="text-sm font-medium text-slate-500 leading-relaxed">{step.desc}</p>
                </div>
                
                {/* Mobile linking arrows */}
                {index < steps.length - 1 && (
                  <div className="lg:hidden flex justify-center mt-6 mb-2">
                    <svg className="w-6 h-6 text-slate-300 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>

      <style>{`
        @keyframes gradientSlide {
          0% { background-position: 100% 0%; }
          100% { background-position: -100% 0%; }
        }
      `}</style>
    </section>
  )
}
